import React from "react";
import styles from "./SuggestionListItem.module.css";

// Define the structure of the suggestion object
export interface Suggestion {
  id: string | number;
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  inspectionFrom?: number;
  inspectionForks?: number;
  historyItems?: string[];
  // We'll use inspectionForks as our score for emoji display
}

interface SuggestionListItemProps {
  suggestion: Suggestion;
  isActive: boolean;
  activeRef?: React.RefObject<HTMLLIElement | null> | null;
  onClick: (suggestion: Suggestion) => void;
  searchQuery: string; // Add search query for highlighting
}

// Function to get emoji based on score (using inspectionForks)
const getScoreEmoji = (score?: number): string => {
  if (!score) return "❓"; // Default emoji
  
  // Map score to emoji
  if (score <= 1) return "💀"; // Worst
  if (score <= 2) return "🤮";
  if (score <= 3) return "🙂";
  if (score <= 4) return "😃";
  return "😍"; // Best
};

// Function to highlight matching text
const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query.trim()) return <>{text}</>;
  
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? <span key={i} className={styles.highlight}>{part}</span> : part
      )}
    </>
  );
};

export const SuggestionListItem: React.FC<SuggestionListItemProps> = ({
  suggestion,
  isActive,
  activeRef,
  onClick,
  searchQuery,
}) => {
  const scoreEmoji = getScoreEmoji(suggestion.inspectionForks);
  
  return (
    <li
      ref={activeRef}
      className={`${styles.listItem} ${isActive ? styles.active : ""} suggestion-item`}
      onClick={() => onClick(suggestion)}
      title={`${suggestion.name}${suggestion.address ? `, ${suggestion.address}` : ""}`}
    >
      <div className={styles.content}>
        <span className={styles.emoji}>{scoreEmoji}</span>
        <div className={styles.textContent}>
          <span className={styles.name}>
            {highlightMatch(suggestion.name, searchQuery)}
          </span>
          {suggestion.address && (
            <span className={styles.address}>
              {highlightMatch(suggestion.address, searchQuery)}
            </span>
          )}
        </div>
      </div>
    </li>
  );
};

// Component for status messages like "Loading..." and "No results found"
interface StatusItemProps {
  message: string;
}

export const StatusItem: React.FC<StatusItemProps> = ({ message }) => {
  return (
    <li className={`${styles.listItem} suggestion-item`} style={{ color: "#9ca3af" /* text-gray-400 */ }}>
      {message}
    </li>
  );
};